/* eslint-disable react/self-closing-comp */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { ITopNavProps } from './top-nav.type';
import { NotificationPopup } from './components';
import Logo from '../../components/logo/logo.component';
import MenuHeader from './components/menu-header/menu-header';
import { Bell, Moon, NotebookPen, Search, Sun, Coins } from 'lucide-react';
import { fetchBalance } from '../../api';
import { useOnClickOutside } from 'usehooks-ts';
import { Link, useNavigate } from 'react-router-dom';
import { routePaths } from '../../routes/routes-path';
import { useDarkMode } from '../../hooks';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';
import { SignalRContext } from '../../context/signalR';
import { HubEvent } from '../../enums/hubnames';
import MobileMenu from './components/mobile-menu.component';

export const TopNav: React.FC<ITopNavProps> = () => {
  const profile = useSelector(
    (state: RootState) => state.features.auth.profile
  );
  const isAuthenticated = useSelector(
    (state: RootState) => state.features.auth.profile
  );

  const [isShowNotifyPopup, setShowNotifyPopup] = useState<boolean>(false);
  const [isNewNoti, setIsNewNoti] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [caBalance, setCaBalance] = useState<number | null>(null); // số dư Ca hiện tại (chip top-nav)
  const navigate = useNavigate();

  // Nạp số dư Ca để hiện ở thanh trên (user luôn biết đang có bao nhiêu).
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchBalance()
      .then((r: any) => setCaBalance(r?.balance ?? 0))
      .catch(() => {});
  }, [isAuthenticated]);

  const submitSearch = () => {
    const q = searchValue.trim();
    if (!q) return;
    navigate(`${routePaths.search}?q=${encodeURIComponent(q)}`);
  };

  const { theme, toggleTheme } = useDarkMode();

  const notificationRef = useRef(null);
  const {
    state: { connectionNoti },
  } = useContext(SignalRContext);

  useOnClickOutside(notificationRef, () => {
    setShowNotifyPopup(false);
  });
  useEffect(() => {
    connectionNoti?.on(HubEvent.NewNotification, (message: any) => {
      setIsNewNoti(true);
    });
  }, [connectionNoti]);

  return (
    <header className="z-[100] h-[--m-top] fixed top-0 left-0 w-full flex items-center bg-white/80 sky-50 backdrop-blur-xl border-b border-slate-200 dark:bg-dark2 dark:border-slate-800">
      <div className="flex items-center w-full xl:px-6 px-2 max-lg:gap-10">
        <div className="w-fit ">
          <Logo />
        </div>
        <div className="flex flex-1 relative justify-between ">
          <div className="max-w-[1040px] ml-4 flex items-center">
            <div
              id="search--box"
              className="xl:w-[680px] sm:w-96 sm:relative rounded-[32px] overflow-hidden z-20 bg-secondery max-md:hidden w-screen left-0 max-sm:fixed max-sm:top-2 dark:!bg-white/5"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <input
                type="text"
                placeholder="Tìm trên Cab.vn"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitSearch();
                }}
                className="w-full !pl-4 !pr-10 !font-normal !bg-transparent h-10 !text-sm outline-none text-[#657384] dark:text-white"
              />
              <button
                type="button"
                aria-label="Tìm kiếm"
                onClick={submitSearch}
                className="absolute top-1/2 right-3 -translate-y-1/2 text-[#657384] dark:text-white"
              >
                <Search size={20} />
              </button>
            </div>
          </div>
          <div className="flex items-center sm:gap-4 gap-2   text-black">
            <button
              className="hidden rounded-md  justify-center items-center gap-2 h-10 text-sm px-4 py-2 text-primary-6 dark:text-primary-3"
              // target="https://www.facebook.com/cabcomeback"
              title="Phản hồi"
            >
              <NotebookPen />
            </button>

            <Link
              to={routePaths.search}
              aria-label="Tìm kiếm"
              className="md:hidden p-1 rounded-full text-[#657384] dark:text-white"
            >
              <Search size={22} />
            </Link>

            {isAuthenticated && caBalance !== null && (
              <Link
                to={routePaths.wallet}
                title="Ví Ca — xem số dư & quản lý"
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400 px-3 py-1.5 text-sm font-bold hover:bg-amber-100 dark:hover:bg-amber-500/25 transition"
              >
                <Coins size={18} />
                {caBalance.toLocaleString('vi-VN')} Ca
              </Link>
            )}

            {isAuthenticated && (
              <div className="relative">
                <button
                  className="sm:p-2 p-1 rounded-full sm:bg-secondery dark:text-white"
                  type="button"
                  onClick={() => {
                    setShowNotifyPopup(!isShowNotifyPopup);
                    setIsNewNoti(false);
                  }}
                >
                  <Bell size={24} />
                  {isNewNoti && (
                    <div className="absolute -z-1 block w-3 h-3 bg-red-500 border-2 border-white rounded-full -top-0 end-0 dark:border-gray-900" />
                  )}
                </button>
                <div ref={notificationRef} className="absolute right-0 top-10">
                  <NotificationPopup
                    visible={isShowNotifyPopup}
                    onClose={() => {
                      setShowNotifyPopup(false);
                    }}
                  />
                </div>
              </div>
            )}
            <button className="sm:p-2 p-1 rounded-full relative sm:bg-secondery dark:text-white">
              {theme === 'dark' ? (
                <Sun
                  size={24}
                  className="cursor-pointer"
                  onClick={toggleTheme}
                />
              ) : (
                <Moon
                  size={24}
                  className="cursor-pointer"
                  onClick={toggleTheme}
                />
              )}
            </button>

            {!isAuthenticated && profile && (
              <div className="ml-3">
                <Link
                  to={routePaths.login}
                  className="px-4 py-2 rounded-md bg-primary text-slate-50 text-sm font-medium mr-3"
                >
                  Đăng nhập
                </Link>
              </div>
            )}
            <div className="ml-3">
              <MenuHeader profile={profile} />
            </div>
          </div>
          <div className="block xl:hidden">
            <MobileMenu />
          </div>
        </div>
      </div>
    </header>
  );
};
